/* prettier-ignore-file */
import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import {
  initiateRoadCall,
  cancelRoadCall,
  setCallExpectation,
  clearCallExpectation,
} from '../db_calls_wrapper';
import { useRoadCallStore } from '../stores';
import { normalizeToE164 } from './roadCall';

/**
 * ContactOptionsModal - Shared modal for calling or texting a customer
 *
 * @param {boolean} visible - Whether the modal is visible
 * @param {function} onClose - Function to close the modal
 * @param {object} customer - Customer object with phone, fullName, firstName, lastName, email, bookingId
 */
const ContactOptionsModal = ({ visible, onClose, customer }) => {
  const navigate = useNavigate();
  const [calling, setCalling] = useState(false);
  const [callbacksBusy, setCallbacksBusy] = useState(false);
  const cancelTicketRef = useRef(null);

  const callExpectations = useRoadCallStore((s) => s.callExpectations);
  const customerE164 = normalizeToE164(customer?.phone);
  const activeCallback = (callExpectations || []).find(
    (e) => e && e.customerPhone === customerE164 && e.expiresAt > Date.now()
  );

  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const customerLabel = () =>
    customer?.fullName || `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || 'this customer';

  const handleCall = async () => {
    if (!customer?.phone) {
      alert('No phone number available');
      return;
    }

    const confirmed = window.confirm(
      `Place Call?\nThis will ring your cell first, then connect to ${formatPhoneDisplay(customer.phone)}.`
    );
    if (!confirmed) return;

    const ticket = { cancelled: false };
    cancelTicketRef.current = ticket;
    setCalling(true);

    const result = await initiateRoadCall({
      customerPhone: customer.phone,
      customerName: customerLabel() !== 'this customer' ? customerLabel() : '',
      bookingId: customer.bookingId || customer.id || '',
    });

    if (ticket.cancelled) {
      if (result?.callSid) {
        cancelRoadCall({ callSid: result.callSid, customerPhone: customer.phone }).catch(() => {});
      }
      return;
    }

    setCalling(false);
    if (result?.success) {
      onClose();
    } else {
      alert(result?.error || 'Could not start the call.');
    }
  };

  const handleCancelCalling = () => {
    if (cancelTicketRef.current) cancelTicketRef.current.cancelled = true;
    setCalling(false);
  };

  const handleAllowCallbacks = async () => {
    if (!customer?.phone) return;
    const confirmed = window.confirm(
      `Allow Callbacks?\nFor the next 30 minutes, any incoming call from ${customerLabel()} will ring your phone directly.\nYou may turn off callbacks at any time.`
    );
    if (!confirmed) return;

    setCallbacksBusy(true);
    const result = await setCallExpectation({
      customerPhone: customer.phone,
      customerName: customerLabel() !== 'this customer' ? customerLabel() : '',
      bookingId: customer.bookingId || customer.id || '',
    });
    setCallbacksBusy(false);
    if (!result?.success) {
      alert(result?.error || 'Failed to set callback window.');
    }
  };

  const handleDisallowCallbacks = async () => {
    if (!customer?.phone) return;
    const confirmed = window.confirm(
      `Disallow Callbacks?\nIncoming calls from ${customerLabel()} will no longer ring your phone.`
    );
    if (!confirmed) return;

    setCallbacksBusy(true);
    await clearCallExpectation(customer.phone);
    setCallbacksBusy(false);
  };

  const handleText = () => {
    if (!customer?.phone) {
      alert('No phone number available');
      return;
    }

    navigate('/messages', {
      state: {
        customer: {
          id: customer.phone,
          phone: customer.phone,
          fullName: customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
          firstName: customer.firstName || '',
          lastName: customer.lastName || '',
          email: customer.email || '',
          bookingId: customer.bookingId || customer.id,
        }
      }
    });
    onClose();
  };

  return (
    <>
    <Modal
      visible={visible && !calling}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {/* Customer Name */}
          <Text style={styles.customerName}>
            {customer?.fullName || `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || 'Customer'}
          </Text>

          {/* Phone Number */}
          {customer?.phone && (
            <Text style={styles.phoneNumber}>{formatPhoneDisplay(customer.phone)}</Text>
          )}

          {/* Buttons */}
          <View style={styles.buttonsContainer}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.callButton]}
              onPress={handleCall}
            >
              <Text style={styles.buttonIcon}>📞</Text>
              <Text style={styles.buttonText}>Call</Text>
            </TouchableOpacity>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.textButton]}
              onPress={handleText}
            >
              <Text style={styles.buttonIcon}>💬</Text>
              <Text style={styles.buttonText}>Text</Text>
            </TouchableOpacity>
          </View>

          {/* Allow Callbacks Toggle */}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.callbacksButton,
              activeCallback && styles.callbacksButtonActive,
            ]}
            onPress={activeCallback ? handleDisallowCallbacks : handleAllowCallbacks}
            disabled={callbacksBusy}
          >
            {callbacksBusy ? (
              <ActivityIndicator size="small" color={activeCallback ? "#FFFFFF" : "#0E7C6B"} />
            ) : (
              <Text style={[styles.callbacksButtonText, activeCallback && styles.callbacksButtonTextActive]}>
                {activeCallback ? 'Callbacks On (tap to cancel)' : 'Allow Callbacks'}
              </Text>
            )}
          </TouchableOpacity>

          {/* Cancel Button */}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.cancelButton}
            onPress={onClose}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>

    {/* Blocking Modal - Calling */}
    <Modal
      visible={visible && calling}
      transparent={true}
      animationType="fade"
      onRequestClose={() => {}}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <ActivityIndicator size="large" color="#0E7C6B" />
          <Text style={styles.callingText}>
            Connecting to {customerLabel()}. Your phone will ring shortly.
          </Text>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.cancelButton}
            onPress={handleCancelCalling}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  customerName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2E3A38',
    textAlign: 'center',
    marginBottom: 8,
  },
  phoneNumber: {
    fontSize: 16,
    color: '#5C6B68',
    textAlign: 'center',
    marginBottom: 24,
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  button: {
    flex: 1,
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  callButton: {
    backgroundColor: '#1F8A5B',
  },
  textButton: {
    backgroundColor: '#0E7C6B',
  },
  buttonIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  callbacksButton: {
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 8,
    backgroundColor: '#F4F6F6',
    borderWidth: 1,
    borderColor: '#0E7C6B',
  },
  callbacksButtonActive: {
    backgroundColor: '#0E7C6B',
    borderColor: '#0E7C6B',
  },
  callbacksButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0E7C6B',
  },
  callbacksButtonTextActive: {
    color: '#FFFFFF',
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#5C6B68',
    fontWeight: '600',
  },
  callingText: {
    marginTop: 16,
    marginBottom: 16,
    fontSize: 15,
    color: '#2E3A38',
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default ContactOptionsModal;
